import type { Task } from '../types/tasks';

export interface DueState {
  label: string; // formatted date
  tone: 'normal' | 'soon' | 'overdue';
  tag?: 'Overdue' | 'Due soon';
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * The backend stores a date-only value coerced to UTC midnight. Reading it with
 * `new Date(iso)` puts a user at a negative UTC offset on the previous calendar
 * day, so the `YYYY-MM-DD` prefix is parsed as LOCAL midnight instead.
 */
const parseDueDate = (iso: string): Date => {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

const startOfToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Classify a task's due date for visual cues. Completed/closed tasks are never
 * flagged as overdue/soon; a task due today is "soon", not overdue.
 */
export const getDueState = (task: Task): DueState | null => {
  if (!task.dueDate) return null;
  const due = parseDueDate(task.dueDate);
  const label = due.toLocaleDateString();
  if (task.status === 'done' || task.closed) return { label, tone: 'normal' };

  const diff = due.getTime() - startOfToday().getTime();
  if (diff < 0) return { label, tone: 'overdue', tag: 'Overdue' };
  if (diff <= 3 * DAY) return { label, tone: 'soon', tag: 'Due soon' };
  return { label, tone: 'normal' };
};

export const DUE_TONE_CLASS: Record<DueState['tone'], string> = {
  normal: 'text-muted-foreground',
  soon: 'text-amber-500',
  overdue: 'text-red-500',
};
